/**
 * VacantReportingLinePanel — set the reporting line of a VACANT seat.
 *
 * A vacant seat has no incumbent, so the worker-anchored ReportingLinePanel
 * (which drives the `employment.LineManager` saga off a personnel number) can't
 * serve it. A position's reporting line is a position→parent PositionHierarchies
 * 'Line' edge in F&O, independent of any occupant — so this panel is
 * POSITION-anchored: the operator picks a PARENT POSITION and we re-point the
 * seat's edge at it.
 *
 * NON-saga: the backend endpoint applies the F&O write synchronously (with a
 * verify-retry) and refreshes the local projection before returning, so there's
 * no submit/poll — a single POST resolves to `{ ok, error }`.
 */

import React, { useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, GitBranch, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import organizationService, {
  type Position,
} from '@/services/api/organizationService';
import { AsyncSearchCombobox } from './AsyncSearchCombobox';

export interface VacantReportingLinePanelProps {
  /** The vacant seat's F&O position code — the seat whose Line edge we re-point. */
  seatPositionCode: string;
  seatTitle?: string;
  currentParentName?: string | null;
  onDone: () => void;
}

export const VacantReportingLinePanel: React.FC<VacantReportingLinePanelProps> = ({
  seatPositionCode,
  seatTitle,
  currentParentName,
  onDone,
}) => {
  const [parent, setParent] = useState<Position | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const searchParents = useMemo(
    () => async (q: string) => {
      const positions = await organizationService.listPositions({
        query: q,
        limit: 20,
        // Only offer live seats as a parent — never point a seat at a RETIRED /
        // FROZEN / PENDING position (list_positions returns every status when
        // unconstrained).
        status: ['ACTIVE', 'VACANT'],
      });
      // A position cannot report to itself.
      return positions.filter((p) => p.position_code !== seatPositionCode);
    },
    [seatPositionCode],
  );

  const submit = async () => {
    if (!parent) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await organizationService.setPositionReportsTo(
        seatPositionCode,
        parent.position_code,
      );
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(res.error || 'The reporting-line change could not be applied in F&O.');
      }
    } catch (err) {
      const message =
        (err as { response?: { data?: { detail?: string } }; message?: string })?.response
          ?.data?.detail ||
        (err as { message?: string })?.message ||
        'Something went wrong applying the reporting-line change.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Success ────────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">Reporting line updated</p>
            <p className="mt-0.5">
              This seat now reports to{' '}
              <span className="font-medium">
                {parent?.incumbent_name || parent?.title || parent?.position_code}
              </span>
              .
            </p>
            <p className="mt-1.5 text-xs opacity-90">
              F&amp;O updated — the reporting line here is refreshed.
            </p>
          </div>
        </div>

        <Button type="button" className="w-full sm:w-auto" onClick={onDone}>
          Done
        </Button>
      </div>
    );
  }

  // ── Form ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-heritage-blue/10 p-2 text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
          <GitBranch className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">Set reporting line</h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Point this vacant seat{seatTitle ? ` (${seatTitle})` : ''} at a parent
            position. Takes effect immediately in F&amp;O — no effective date.
          </p>
        </div>
      </div>

      {currentParentName && (
        <p className="text-sm text-muted-foreground">
          Current parent:{' '}
          <span className="font-medium text-foreground">{currentParentName}</span>
        </p>
      )}

      <div className="space-y-1.5">
        <Label>Parent position</Label>
        <AsyncSearchCombobox<Position>
          search={searchParents}
          getKey={(p) => p.position_code}
          getPrimary={(p) => p.incumbent_name || 'Vacant'}
          getSecondary={(p) => `${p.title} · ${p.position_code}`}
          onSelect={setParent}
          placeholder="Search positions by title or code…"
          disabled={submitting}
          emptyText="No positions match."
        />
        {parent && (
          <p className="text-xs text-muted-foreground">
            Selected:{' '}
            <span className="font-medium text-foreground">
              {parent.incumbent_name || 'Vacant'}
            </span>{' '}
            ({parent.title} · {parent.position_code})
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive dark:bg-destructive/10">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <p className="min-w-0 break-words">{error}</p>
        </div>
      )}

      <Button
        type="button"
        className="w-full sm:w-auto"
        onClick={submit}
        disabled={!parent || submitting}
      >
        {submitting ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            Applying…
          </>
        ) : (
          <>
            <GitBranch className="mr-1.5 h-4 w-4" />
            Set reporting line
          </>
        )}
      </Button>
    </div>
  );
};

export default VacantReportingLinePanel;
